import { roundToN } from 'utilities';
import { AdjustProbabilityValuesParams, ChangeItem } from './types';

const getProbabilityCells = (cells: AdjustProbabilityValuesParams['cells'], probabilityCellAlias: string) => (
  Object.values(cells).filter(currentCell => {
    const matchesAlias = probabilityCellAlias === ''
      // Get all cells for the allocation scenario columns
      // (they are already filtered out by the caller)
      ? true
      // Condition for the summary weighting/probability cells
      : currentCell?.alias === probabilityCellAlias;
    return matchesAlias && !currentCell.readOnly;
  })
);

const isOutOfRange = (value: number) => value < 0 || value > 1;

const getCellValue = (cell: ChangeItem['cell']) => Number(cell.value as string) || 0;

// Reject the edit outright instead of clamping it into range.
// Revert the cell to its previous value so nothing changes.
const revertOutOfRangeEdit = (changedCell: AdjustProbabilityValuesParams['changedCell']) => (
  [{ cell: changedCell, value: getCellValue(changedCell) }]
);

const setAllOthersToZero = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCell: AdjustProbabilityValuesParams['changedCell']
) => (
  probabilityCells
    .filter(probCell => probCell.key !== changedCell.key)
    .map(probCell => ({ cell: probCell, value: 0 }))
);

const rebalanceOtherCell = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCell: AdjustProbabilityValuesParams['changedCell'],
  numericValue: number
) => {
  const otherCell = probabilityCells.find(c => c !== changedCell);
  if (!otherCell) {
    return [];
  }
  // Round to 6 decimal places to avoid floating point precision issues
  return [{ cell: otherCell, value: roundToN(1 - numericValue, 6) }];
};

// Sum every cell except the one being edited, then add its new value back in.
const getTotalAfterChange = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCell: AdjustProbabilityValuesParams['changedCell'],
  numericValue: number
) => (
  probabilityCells.reduce((sum, cell) => (
    cell.key === changedCell.key ? sum : sum + getCellValue(cell)
  ), numericValue)
);

// If the last cell was changed, the first cell takes the hit; otherwise the last cell does.
const getCellAbsorbingDifference = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCellIndex: number
) => (
  changedCellIndex === probabilityCells.length - 1
    ? probabilityCells[0]
    : probabilityCells.at(-1)
);

const rebalanceCellAbsorbingDifference = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCell: AdjustProbabilityValuesParams['changedCell'],
  changedCellIndex: number,
  numericValue: number
) => {
  const difference = getTotalAfterChange(probabilityCells, changedCell, numericValue) - 1;
  if (difference === 0) {
    return [];
  }

  const cellToAdjust = getCellAbsorbingDifference(probabilityCells, changedCellIndex);
  if (!cellToAdjust) {
    return [];
  }

  const newValue = Math.max(0, getCellValue(cellToAdjust) - difference);
  // Round to 6 decimal places to avoid floating point precision issues
  return [{ cell: cellToAdjust, value: roundToN(newValue, 6) }];
};

const getSiblingChanges = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCell: AdjustProbabilityValuesParams['changedCell'],
  changedCellIndex: number,
  numericValue: number
) => {
  // Setting a cell to 100% means every other scenario is 0%.
  if (numericValue === 1) {
    return setAllOthersToZero(probabilityCells, changedCell);
  }
  // With only two cells, the other one always absorbs the full difference.
  if (probabilityCells.length === 2) {
    return rebalanceOtherCell(probabilityCells, changedCell, numericValue);
  }
  // With three or more cells, a single cell absorbs the difference instead.
  return rebalanceCellAbsorbingDifference(probabilityCells, changedCell, changedCellIndex, numericValue);
};

const rebalanceProbabilityCells = (
  probabilityCells: ReturnType<typeof getProbabilityCells>,
  changedCell: AdjustProbabilityValuesParams['changedCell'],
  changedCellIndex: number,
  numericValue: number
) => {
  const siblingChanges = getSiblingChanges(probabilityCells, changedCell, changedCellIndex, numericValue);
  return [...siblingChanges, { cell: changedCell, value: numericValue }];
};

const findSummaryCell = (
  summaryCellAlias: AdjustProbabilityValuesParams['summaryCellAlias'],
  summarySpreadsheet: AdjustProbabilityValuesParams['summarySpreadsheet'],
  changedCell: ChangeItem['cell']
) => {
  if (!summaryCellAlias || !summarySpreadsheet) {
    return undefined;
  }
  return Object.values(summarySpreadsheet).find(
    sCell => sCell.alias === summaryCellAlias && sCell.columnLegend === changedCell.columnLegend
  );
};

const replicateToSummaryCells = (
  cellChanges: ChangeItem[],
  summaryCellAlias: AdjustProbabilityValuesParams['summaryCellAlias'],
  summarySpreadsheet: AdjustProbabilityValuesParams['summarySpreadsheet']
) => {
  const allChanges: ChangeItem[] = [];

  cellChanges.forEach(changeItem => {
    allChanges.push(changeItem);

    const summaryCell = findSummaryCell(summaryCellAlias, summarySpreadsheet, changeItem.cell);
    if (summaryCell) {
      allChanges.push({ cell: summaryCell, value: changeItem.value });
    }
  });

  return allChanges;
};

/**
 * Adjusts probability/weighting values to maintain a sum of 1.
 * The cases below come from a request Matt T. made via Slack,
 * which is documented in SCAL-6218.
 * @param params - Parameters for probability adjustments
 * @returns Array of cell changes
 */
const adjustProbabilityValues = (params: AdjustProbabilityValuesParams) => {
  const {
    cells,
    changedCell,
    newValue,
    probabilityCellAlias,
    summaryCellAlias,
    summarySpreadsheet,
  } = params;

  const probabilityCells = getProbabilityCells(cells, probabilityCellAlias);
  const changedCellIndex = probabilityCells.indexOf(changedCell);
  const rawValue = Number(newValue);

  const cellChanges = isOutOfRange(rawValue)
    ? revertOutOfRangeEdit(changedCell)
    : rebalanceProbabilityCells(probabilityCells, changedCell, changedCellIndex, rawValue);

  return replicateToSummaryCells(cellChanges, summaryCellAlias, summarySpreadsheet);
};

export default adjustProbabilityValues;
